// Tiny DOM helpers — no framework. Views build HTML strings (escaped) and wire
// events on mount via delegation or element refs.

/** Escape untrusted text for safe interpolation into innerHTML. */
export function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

export function qs<T extends HTMLElement = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}
export function qsa<T extends HTMLElement = HTMLElement>(root: ParentNode, sel: string): T[] {
  return Array.from(root.querySelectorAll<T>(sel));
}

/** Attach a click handler to every element matching sel within root. */
export function onClick(root: ParentNode, sel: string, fn: (el: HTMLElement, ev: Event) => void) {
  qsa(root, sel).forEach((el) => el.addEventListener('click', (ev) => fn(el, ev)));
}

let toastTimer: any;
export function toast(message: string, kind: 'info' | 'err' = 'info', ms = 2600) {
  document.querySelector('.toast')?.remove();
  const t = document.createElement('div');
  t.className = 'toast' + (kind === 'err' ? ' err' : '');
  t.setAttribute('role', kind === 'err' ? 'alert' : 'status');
  t.textContent = message;
  document.body.appendChild(t);
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.remove(), ms);
}

export function confirmDialog(message: string): boolean {
  return typeof confirm === 'function' ? confirm(message) : true;
}

export interface FieldSpec {
  name: string;
  label: string;
  type?: string; // text | number | select | textarea
  value?: string;
  placeholder?: string;
  required?: boolean;
  pattern?: string;
  maxlength?: number;
  inputmode?: string;
  hint?: string;
  options?: Array<{ value: string; label: string }>;
}

/** Accessible modal form built on <dialog>. Resolves to values, or null if cancelled. */
export function formDialog(
  title: string,
  fields: FieldSpec[],
  submitLabel = 'Save',
): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    const dlg = document.createElement('dialog');
    dlg.className = 'dlg';
    const body = fields
      .map((f) => {
        const id = 'f_' + f.name;
        let control: string;
        if (f.type === 'select') {
          control = `<select class="input" id="${id}" name="${esc(f.name)}" ${f.required ? 'required' : ''}>${(f.options ?? [])
            .map((o) => `<option value="${esc(o.value)}" ${o.value === f.value ? 'selected' : ''}>${esc(o.label)}</option>`)
            .join('')}</select>`;
        } else if (f.type === 'textarea') {
          control = `<textarea class="input" id="${id}" name="${esc(f.name)}" rows="5" ${f.required ? 'required' : ''} placeholder="${esc(f.placeholder ?? '')}">${esc(f.value ?? '')}</textarea>`;
        } else {
          control = `<input class="input ${f.inputmode ? 'mono' : ''}" id="${id}" name="${esc(f.name)}" type="${esc(f.type ?? 'text')}"
            value="${esc(f.value ?? '')}" placeholder="${esc(f.placeholder ?? '')}"
            ${f.required ? 'required' : ''} ${f.pattern ? `pattern="${esc(f.pattern)}"` : ''}
            ${f.maxlength ? `maxlength="${f.maxlength}"` : ''} ${f.inputmode ? `inputmode="${esc(f.inputmode)}"` : ''} />`;
        }
        return `<div class="field"><label for="${id}">${esc(f.label)}</label>${control}${f.hint ? `<p class="hint">${esc(f.hint)}</p>` : ''}</div>`;
      })
      .join('');
    dlg.innerHTML = `<form method="dialog"><h2 class="dlg-title">${esc(title)}</h2>${body}
      <div class="dlg-actions"><button type="button" class="btn btn-ghost" value="cancel" data-cancel>Cancel</button>
      <button type="submit" class="btn btn-primary" data-ok>${esc(submitLabel)}</button></div></form>`;
    document.body.appendChild(dlg);
    const form = dlg.querySelector('form')!;
    const cleanup = () => dlg.remove();
    dlg.querySelector('[data-cancel]')!.addEventListener('click', () => {
      dlg.close();
      cleanup();
      resolve(null);
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!form.reportValidity()) return;
      const data: Record<string, string> = {};
      fields.forEach((f) => {
        const elx = form.elements.namedItem(f.name) as HTMLInputElement | null;
        data[f.name] = (elx?.value ?? '').trim();
      });
      dlg.close();
      cleanup();
      resolve(data);
    });
    dlg.addEventListener('cancel', () => {
      cleanup();
      resolve(null);
    });
    dlg.showModal();
    (dlg.querySelector('.input') as HTMLElement | null)?.focus();
  });
}
